import { Grid, Box } from "@mui/material";
import { Event } from "@root/utils/entities";
import { observer, useLocalObservable } from "mobx-react-lite";
import { EventPagination } from "./EventPagination";
import { PaginationStore } from "@root/stores/paginationStore";
import { EventListItem } from "./EventListItem";
import { useEffect, useRef, useState } from "react";
import { useAccountContext } from "@root/stores/accountStore";
import { ModalStore } from "@root/stores/modalStore";
import { ShareModal } from "@root/components/modals/ShareModal";
import { getEventShareUrl } from "@root/utils/functions";

interface EventListProps {
  events: Event[];
  paginationStore: PaginationStore;
  onPageChange: (newPage: number) => void;
  onViewEvent: (event: Event) => void;
  onHideEvent?: (eventId: string, isHidden: boolean) => void;
}

export const EventList = observer(
  ({
    events,
    paginationStore,
    onPageChange,
    onViewEvent,
    onHideEvent,
  }: EventListProps) => {
    const accountStore = useAccountContext();
    const currentPage = paginationStore.page || 0;
    const containerRef = useRef<HTMLDivElement>(null);
    const shareModalStore = useLocalObservable(() => new ModalStore());
    const [eventToShare, setEventToShare] = useState<Event | null>(null);

    const handleShare = (event: Event) => {
      setEventToShare(event);
      shareModalStore.open();
    };

    // Scroll to top when page changes
    useEffect(() => {
      if (containerRef.current) {
        // Try to find the scrollable parent container
        let element: HTMLElement | null = containerRef.current.parentElement;
        while (element) {
          const style = window.getComputedStyle(element);
          if (
            style.overflowY === "auto" ||
            style.overflowY === "scroll" ||
            element.id === "event-list-scroll-container"
          ) {
            element.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
          element = element.parentElement;
        }
        // Fallback: scroll window on mobile
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, [currentPage]);

    return (
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Event List */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            width: "100%",
            minHeight: 0,
          }}
        >
          <Grid container sx={{ py: 0 }} spacing={2}>
            {events?.map((event, index) => {
              // Check if event is created by current user
              const isCreatedByCurrentUser = accountStore.isUserProfile
                ? !!accountStore.account?.id &&
                  !event.organizationId &&
                  event.user?.id === accountStore.account.id
                : !!accountStore.session?.id &&
                  event.organizationId === accountStore.session.id;

              // Organizations cannot follow or hide events
              const isOrg = accountStore.isOrganizationProfile;

              return (
                <Grid
                  item
                  key={`event${index}_${event.id.substring(0, 4)}`}
                  xs={12}
                >
                  <EventListItem
                    event={event}
                    onView={() => onViewEvent(event)}
                    onHideChange={onHideEvent}
                    showHideIcon={!isCreatedByCurrentUser && !isOrg}
                    showFollowIcon={!isCreatedByCurrentUser && !isOrg}
                    showShareIcon={!!isCreatedByCurrentUser || isOrg}
                    onShare={() => handleShare(event)}
                  />
                </Grid>
              );
            })}
          </Grid>
          <EventPagination
            events={events}
            store={paginationStore}
            onChange={onPageChange}
          />
        </Box>

        <ShareModal
          store={shareModalStore}
          title="Share Event"
          url={eventToShare ? getEventShareUrl(eventToShare.id) : ""}
        />
      </Box>
    );
  }
);
